// Static admin-authored HTML block (legacy Alpaca markup). The body is
// sanitized with DOMPurify before rendering — admin-authored content is
// generally trusted, but markup also surfaces in agent / micro-frontend hosts
// where the source is less certain, so we never inject raw HTML. When the
// importer tagged the block as an alert (config.style === "alert", config.level
// the bootstrap suffix) we wrap it in the matching Bootstrap alert.

import DOMPurify from 'dompurify'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { MarkupConfig } from '../types'
import { resolveWidgetComponent } from '../widgets'

export function MarkupField(props: FieldComponentProps) {
  const { field } = props
  const config = field.config as MarkupConfig | undefined
  const { widgetComponents } = useFieldContext()

  // Passthrough widgets (country/state/image/chem_*) arrive as markup carrying
  // config.widget — route them to a registered widget or a built-in fallback.
  if (config?.widget) {
    const Widget = resolveWidgetComponent(config.widget, widgetComponents)
    if (Widget) return <Widget {...props} />
    // No widget available: fall through to raw markup body below.
  }

  const body = config?.body
  if (!body) return null

  const clean = DOMPurify.sanitize(body)

  if (config?.style === 'alert') {
    const level = config.level && /^[a-z-]+$/.test(config.level) ? config.level : 'info'
    return (
      <div className={`alert alert-${level}`} role="alert" dangerouslySetInnerHTML={{ __html: clean }} />
    )
  }

  return <div className="dff-markup" dangerouslySetInnerHTML={{ __html: clean }} />
}
